import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";

const ThemeContext = createContext({
  theme: "light",
  mode: "auto",
  setTheme: () => {},
  toggle: () => {},
  setMode: () => {},
});

// mode is what the user chose: "light" | "dark" | "auto" (default).
// theme is what is currently applied to <html>.
const MODE_KEY = "freelanzen_theme_mode";
const LEGACY_KEY = "freelanzen_theme";       // legacy — pre-timer versions
const NIGHT_START = 20; // 20:00 → dark
const NIGHT_END = 7;    // < 07:00 → still dark

function isNightNow(now = new Date()) {
  const h = now.getHours();
  return h >= NIGHT_START || h < NIGHT_END;
}

function resolveTheme(mode) {
  if (mode === "dark") return "dark";
  if (mode === "light") return "light";
  // auto: time-of-day. Fallback to system pref only outside night window
  // when the user is on a device with an explicit dark preference.
  if (isNightNow()) return "dark";
  if (typeof window !== "undefined" && window.matchMedia
      && window.matchMedia("(prefers-color-scheme: dark)").matches) {
    return "dark";
  }
  return "light";
}

function readInitialMode() {
  try {
    const m = localStorage.getItem(MODE_KEY);
    if (m === "light" || m === "dark" || m === "auto") return m;
    // Legacy migration: an earlier version stored just "light"/"dark".
    // Treat that as an explicit user choice.
    const legacy = localStorage.getItem(LEGACY_KEY);
    if (legacy === "light" || legacy === "dark") {
      localStorage.setItem(MODE_KEY, legacy);
      return legacy;
    }
  } catch { /* ignore */ }
  return "auto";
}

function getInitialTheme() {
  if (typeof document !== "undefined" && document.documentElement.classList.contains("dark")) {
    return "dark";
  }
  return "light";
}

export function ThemeProvider({ children }) {
  const [mode, setModeState] = useState(readInitialMode);
  const [theme, setThemeState] = useState(getInitialTheme);
  const tickRef = useRef(null);

  const applyTheme = useCallback((next) => {
    const root = document.documentElement;
    if (next === "dark") root.classList.add("dark");
    else root.classList.remove("dark");
    root.style.colorScheme = next;
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute("content", next === "dark" ? "#1A2530" : "#419A8E");
  }, []);

  const refresh = useCallback(() => {
    const next = resolveTheme(mode);
    setThemeState((prev) => (prev === next ? prev : next));
    applyTheme(next);
  }, [mode, applyTheme]);

  // Re-evaluate theme every minute so auto-mode flips at 20:00 / 07:00
  // even if the user stays on the same tab across the boundary.
  useEffect(() => {
    refresh();
    if (mode !== "auto") return;
    tickRef.current = setInterval(refresh, 60_000);
    return () => {
      if (tickRef.current) clearInterval(tickRef.current);
      tickRef.current = null;
    };
  }, [mode, refresh]);

  // Follow OS preference changes only while in auto mode outside the night
  // window (night always wins).
  useEffect(() => {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => { if (mode === "auto") refresh(); };
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, [mode, refresh]);

  const setMode = useCallback((next) => {
    setModeState(next);
    try { localStorage.setItem(MODE_KEY, next); } catch { /* ignore */ }
  }, []);

  // Manual toggle: pins the theme (opposite of current). Sets explicit mode
  // so the night timer no longer overrides the user's choice.
  const toggle = useCallback(() => {
    const next = theme === "dark" ? "light" : "dark";
    setMode(next);
  }, [theme, setMode]);

  const setTheme = useCallback((next) => {
    setMode(next);
  }, [setMode]);

  return (
    <ThemeContext.Provider value={{ theme, mode, setTheme, setMode, toggle }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
